// src/utils/generateInvoicePDF.js
//
// Однополосный PDF-документ оптовой накладной:
//   - Шапка с данными магазина
//   - Данные клиента
//   - Таблица позиций (№, Товар, Кол-во, Ед., Цена, Сумма)
//   - Итого
//   - Заметка (если есть)
//   - Места для подписей
//
// Использует jsPDF + autoTable + Roboto (кириллица).

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { savePDF } from './savePDF';
import { displayCode } from './marking';

let cachedFont = null;
async function loadCyrillicFont() {
  if (cachedFont) return cachedFont;
  try {
    const ttfUrl = 'https://cdn.jsdelivr.net/npm/roboto-font@0.1.0/fonts/Roboto/roboto-regular-webfont.ttf';
    const res = await fetch(ttfUrl);
    if (!res.ok) throw new Error('Font fetch failed');
    const buf = await res.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buf);
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    cachedFont = btoa(binary);
    return cachedFont;
  } catch (e) {
    console.warn('Шрифт кириллицы не загружен:', e);
    return null;
  }
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmtMoney(n) {
  return (Number(n) || 0).toLocaleString('ru', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Преобразование числа в текст (для строки "Итого прописью") — простая версия
function moneyToWords(amount) {
  // Минимальная защита — не упасть. Если нужно красиво — потом усилим.
  const whole = Math.floor(amount);
  const cents = Math.round((amount - whole) * 100);
  return `${whole.toLocaleString('ru')} (целых) и ${String(cents).padStart(2, '0')} коп.`;
}

/**
 * Генерирует PDF-накладную и предлагает скачать.
 * @param {Object} params
 *   - sale: { id, number, createdAt, total, note, items: [{ name, qty, unit, salePrice, total }] }
 *   - counterparty: { name, phone, whatsapp, email, address }
 *   - shop: { name, address?, phone?, photo? }
 *   - currency: 'KGS' | 'USD' | ... (символ или код)
 * @param {Object} t — i18n translator (req.). Если null — используются русские дефолты.
 */
export async function generateInvoicePDF({ sale, counterparty, shop, currency }, t) {
  const tr = (key, fallback) => (t ? t(key, fallback) : fallback);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // Подключаем шрифт
  const fontB64 = await loadCyrillicFont();
  if (fontB64) {
    doc.addFileToVFS('Roboto-Regular.ttf', fontB64);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.setFont('Roboto');
  }

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = 16;

  // ─── Шапка ───────────────────────────────────────────────
  doc.setFontSize(18);
  doc.setTextColor(20);
  doc.text(tr('invoicePdf.title', `Накладная №${sale.number}`).replace('{{number}}', sale.number), margin, y);
  y += 7;

  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(`${tr('invoicePdf.dateLabel', 'Дата')}: ${fmtDate(sale.createdAt)}`, margin, y);
  y += 8;

  // ─── Данные магазина (Поставщик) ────────────────────────
  doc.setTextColor(20);
  doc.setFontSize(11);
  doc.text(tr('invoicePdf.from', 'От кого:'), margin, y);
  y += 5;
  doc.setFontSize(10);
  doc.text(shop?.name || '—', margin, y);
  y += 5;
  if (shop?.address) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(shop.address, margin, y);
    doc.setTextColor(20);
    y += 5;
  }
  if (shop?.phone) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`${tr('invoicePdf.phone', 'Тел.')}: ${shop.phone}`, margin, y);
    doc.setTextColor(20);
    y += 5;
  }
  y += 3;

  // ─── Данные клиента (Получатель) ────────────────────────
  doc.setFontSize(11);
  doc.text(tr('invoicePdf.to', 'Кому:'), margin, y);
  y += 5;
  doc.setFontSize(10);
  doc.text(counterparty?.name || '—', margin, y);
  y += 5;
  if (counterparty?.address) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(counterparty.address, margin, y);
    doc.setTextColor(20);
    y += 5;
  }
  const contactLine = [counterparty?.phone, counterparty?.whatsapp].filter(Boolean).join(' · ');
  if (contactLine) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`${tr('invoicePdf.phone', 'Тел.')}: ${contactLine}`, margin, y);
    doc.setTextColor(20);
    y += 5;
  }
  y += 4;

  // ─── Таблица позиций ────────────────────────────────────
  autoTable(doc, {
    startY: y,
    head: [[
      '№',
      tr('invoicePdf.colName', 'Наименование'),
      tr('invoicePdf.colQty', 'Кол-во'),
      tr('invoicePdf.colUnit', 'Ед.'),
      tr('invoicePdf.colPrice', 'Цена'),
      tr('invoicePdf.colSum', 'Сумма'),
    ]],
    body: sale.items.map((it, idx) => [
      String(idx + 1),
      it.name,
      String(it.qty),
      it.unit,
      fmtMoney(it.salePrice),
      fmtMoney(it.total),
    ]),
    foot: [[
      '', tr('invoicePdf.totalLabel', 'ИТОГО'), '', '', '',
      fmtMoney(sale.total),
    ]],
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: [240, 240, 250], textColor: 20, fontStyle: 'bold', fontSize: 11 },
    columnStyles: {
      0: { halign: 'right', cellWidth: 10 },
      2: { halign: 'right' },
      3: { halign: 'center' },
      4: { halign: 'right' },
      5: { halign: 'right', fontStyle: 'bold' },
    },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 6;

  // ─── Итого с валютой ────────────────────────────────────
  doc.setFontSize(12);
  doc.setFont('Roboto', 'normal');
  const totalLine = `${tr('invoicePdf.totalLabel', 'Итого')}: ${fmtMoney(sale.total)} ${currency || ''}`;
  // Печатаем справа
  const totalWidth = doc.getTextWidth(totalLine);
  doc.text(totalLine, pageWidth - margin - totalWidth, y);
  y += 6;

  // Прописью
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`${tr('invoicePdf.inWords', 'Сумма прописью')}: ${moneyToWords(sale.total)} ${currency || ''}`, margin, y);
  doc.setTextColor(20);
  y += 8;

  // ─── Заметка ─────────────────────────────────────────────
  if (sale.note) {
    doc.setFontSize(10);
    doc.text(tr('invoicePdf.note', 'Примечание') + ':', margin, y);
    y += 5;
    doc.setFontSize(9);
    doc.setTextColor(80);
    const noteLines = doc.splitTextToSize(sale.note, pageWidth - 2 * margin);
    doc.text(noteLines, margin, y);
    doc.setTextColor(20);
    y += noteLines.length * 4 + 4;
  }

  // ─── Коды DataMatrix ─────────────────────────────────────
  if (Array.isArray(sale.codes) && sale.codes.length) {
    y += 2;
    doc.setFontSize(11);
    doc.setTextColor(20);
    doc.text(`${tr('invoicePdf.codesTitle', 'Коды DataMatrix')} (${sale.codes.length})`, margin, y);
    y += 2;
    autoTable(doc, {
      startY: y,
      head: [[
        '№',
        tr('invoicePdf.colName', 'Наименование'),
        tr('invoicePdf.codeCol', 'Код'),
      ]],
      body: sale.codes.map(c => [String(c.seq), c.name || '—', displayCode(c.code)]),
      theme: 'grid',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.5, overflow: 'linebreak' },
      headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { halign: 'right', cellWidth: 10 },
        1: { cellWidth: 50 },
        2: { cellWidth: 'auto' },
      },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  y += 10;

  // ─── Места для подписей ─────────────────────────────────
  doc.setFontSize(10);
  const sigY = y;
  const colWidth = (pageWidth - 2 * margin) / 2;

  doc.text(tr('invoicePdf.sigFrom', 'Отгрузил:'), margin, sigY);
  doc.line(margin + 25, sigY + 1, margin + colWidth - 5, sigY + 1);

  doc.text(tr('invoicePdf.sigTo', 'Получил:'), margin + colWidth, sigY);
  doc.line(margin + colWidth + 25, sigY + 1, pageWidth - margin, sigY + 1);

  // Под линиями — пояснение
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(tr('invoicePdf.sigFromHint', '(подпись, расшифровка)'), margin + 25, sigY + 6);
  doc.text(tr('invoicePdf.sigToHint', '(подпись, расшифровка)'), margin + colWidth + 25, sigY + 6);

  // ─── Сохраняем ────────────────────────────────────────────
  const filename = `${tr('invoicePdf.filename', 'Накладная')}_${sale.number}_${fmtDate(sale.createdAt).replace(/\./g, '-')}.pdf`;

  // savePDF — общая утилита проекта, на Android использует Capacitor Filesystem,
  // в web — обычное скачивание. Возвращает { native: bool }
  const blob = doc.output('blob');
  const result = await savePDF(blob, filename);
  return { success: true, filename, native: result?.native || false };
}
